import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { STATUS_WF_TO_PM_STEP, STATUS_WF_TO_CLIENT_STEP, ESTIMATE_WF_TO_PM_STEP, ESTIMATES_BOARD_ID } from './workflowMapping'
import { STAGE_TO_PM_STEP, STAGE_TO_CLIENT_STEP } from './constructionMapping'

// ── Duration formatting ──────────────────────────────────────────────────────

/**
 * Format a duration in milliseconds to a human-readable string.
 * @param {number} ms — duration in milliseconds
 * @param {object} [labels] — optional i18n unit labels
 * @returns {string} e.g. "45m", "2h 30m", "3d 4h", "1w 2d"
 */
export function formatDuration(ms, labels = {}) {
    if (!ms || ms < 0) return ''
    const {
        week = 'w',
        day = 'd',
        hour = 'h',
        minute = 'm',
        lessThan = '< 1m',
    } = labels
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    const weeks = Math.floor(days / 7)

    if (weeks > 0) {
        const remDays = days % 7
        return remDays > 0 ? `${weeks}${week} ${remDays}${day}` : `${weeks}${week}`
    }
    if (days > 0) {
        const remHours = hours % 24
        return remHours > 0 ? `${days}${day} ${remHours}${hour}` : `${days}${day}`
    }
    if (hours > 0) {
        const remMinutes = minutes % 60
        return remMinutes > 0 ? `${hours}${hour} ${remMinutes}${minute}` : `${hours}${hour}`
    }
    if (minutes > 0) {
        return `${minutes}${minute}`
    }
    return lessThan
}

// ── Supabase helpers ─────────────────────────────────────────────────────────

/**
 * Fetch all timers for a project.
 */
export async function fetchStageTimers(projectId, projectType) {
    const { data, error } = await supabase
        .from('workflow_stage_timers')
        .select('*')
        .eq('project_id', projectId)
        .eq('project_type', projectType)
        .order('entered_at', { ascending: true })

    if (error) {
        console.error('[workflowTimers] fetch error:', error)
        return []
    }
    return data || []
}

/**
 * Close the current active timer and open a new one for the given stage.
 * Returns true if a transition was recorded.
 */
export async function recordStageTransition(projectId, projectType, boardId, stageId, stageLabel) {
    if (!projectId || !stageId) return false

    const now = new Date().toISOString()

    // Find active row
    const { data: activeRows, error: activeErr } = await supabase
        .from('workflow_stage_timers')
        .select('*')
        .eq('project_id', projectId)
        .eq('project_type', projectType)
        .is('exited_at', null)
        .order('entered_at', { ascending: false })
        .limit(1)

    if (activeErr) {
        console.error('[workflowTimers] active select error:', activeErr)
        return false
    }

    const activeRow = activeRows?.[0]

    // Same stage — nothing to do
    if (activeRow && activeRow.stage_id === stageId) {
        return false
    }

    // Close old row
    if (activeRow) {
        const enteredAt = new Date(activeRow.entered_at).getTime()
        const durationMs = Date.now() - enteredAt
        const { error: closeErr } = await supabase
            .from('workflow_stage_timers')
            .update({ exited_at: now, duration_ms: durationMs })
            .eq('id', activeRow.id)

        if (closeErr) {
            console.error('[workflowTimers] close error:', closeErr)
        }
    }

    // Insert new row
    const { error: insertErr } = await supabase
        .from('workflow_stage_timers')
        .insert({
            project_id: projectId,
            project_type: projectType,
            board_id: boardId || null,
            stage_id: String(stageId),
            stage_label: stageLabel || '',
            entered_at: now,
            exited_at: null,
            duration_ms: null,
        })

    if (insertErr) {
        console.error('[workflowTimers] insert error:', insertErr)
        return false
    }

    return true
}

/**
 * Build a Map<flowNodeId, timerInfo> from raw Supabase timer rows.
 * @param {Array} timers — raw workflow_stage_timers rows
 * @param {Array} flow — current flow array (PM_FLOW / CLIENT_FLOW / CON_PM_FLOW / CON_CLIENT_FLOW)
 * @param {string} projectType — 'arch' | 'construction'
 * @param {boolean} isPm — true for PM view, false for client view
 * @param {string} [boardId] — Monday board ID (used to select estimate vs project mapping)
 * @returns {Map<string, {totalMs: number, isActive: boolean, visits: Array}>}
 */
export function buildNodeTimerMap(timers, flow, projectType, isPm, boardId) {
    const map = new Map()
    for (const timer of timers) {
        let stepIdx = -1
        if (projectType === 'arch') {
            const statusWfId = Number(timer.stage_id)
            const isEstimates = boardId === ESTIMATES_BOARD_ID
            const stepMap = isEstimates
                ? ESTIMATE_WF_TO_PM_STEP
                : (isPm ? STATUS_WF_TO_PM_STEP : STATUS_WF_TO_CLIENT_STEP)
            stepIdx = stepMap[statusWfId]
        } else if (projectType === 'construction') {
            const stageMap = isPm ? STAGE_TO_PM_STEP : STAGE_TO_CLIENT_STEP
            stepIdx = stageMap[timer.stage_id]
        }
        if (stepIdx === undefined || stepIdx < 0 || stepIdx >= flow.length) continue

        const nodeId = flow[stepIdx].id
        if (!map.has(nodeId)) {
            map.set(nodeId, { totalMs: 0, isActive: false, visits: [] })
        }
        const entry = map.get(nodeId)
        const isActive = !timer.exited_at
        const durationMs = isActive
            ? Date.now() - new Date(timer.entered_at).getTime()
            : (timer.duration_ms || 0)
        entry.totalMs += durationMs
        entry.isActive = entry.isActive || isActive
        entry.visits.push({ durationMs, isActive, enteredAt: timer.entered_at })
    }
    return map
}

// ── Project start date helpers ───────────────────────────────────────────────

export async function fetchProjectStartDate(projectId, projectType) {
    if (!projectId || !projectType) return null
    const { data, error } = await supabase
        .from('project_start_dates')
        .select('created_at')
        .eq('project_id', projectId)
        .eq('project_type', projectType)
        .single()
    if (error && error.code !== 'PGRST116') {
        console.error('[workflowTimers] fetchProjectStartDate error:', error)
    }
    return data?.created_at || null
}

export async function saveProjectStartDate(projectId, projectType, boardId, createdAt) {
    if (!projectId || !projectType || !createdAt) return
    const { error } = await supabase
        .from('project_start_dates')
        .upsert({
            project_id: projectId,
            project_type: projectType,
            board_id: boardId || null,
            created_at: createdAt,
            updated_at: new Date().toISOString(),
        })
    if (error) {
        console.error('[workflowTimers] saveProjectStartDate error:', error)
    }
}

// ── React hook ───────────────────────────────────────────────────────────────

/**
 * useStageTimers — fetch and live-update stage durations for a project.
 *
 * @param {string|null} projectId
 * @param {string} projectType — 'arch' | 'construction'
 * @returns {{
 *   timers: Array,
 *   activeTimer: object|null,
 *   durationsByStage: Map<string, Array<{durationMs, isActive}>>,
 *   liveDuration: string,
 *   loading: boolean,
 *   refresh: () => Promise<void>
 * }}
 */
export function useStageTimers(projectId, projectType) {
    const [timers, setTimers] = useState([])
    const [loading, setLoading] = useState(false)
    const [tick, setTick] = useState(0)
    const intervalRef = useRef(null)

    const refresh = useCallback(async () => {
        if (!projectId) {
            setTimers([])
            return
        }
        setLoading(true)
        const data = await fetchStageTimers(projectId, projectType)
        setTimers(data)
        setLoading(false)
    }, [projectId, projectType])

    // Fetch on mount / project change
    useEffect(() => {
        refresh()
    }, [refresh])

    // Live tick every minute for the active timer
    useEffect(() => {
        if (!projectId) return
        intervalRef.current = setInterval(() => {
            setTick(t => t + 1)
        }, 60_000)
        return () => clearInterval(intervalRef.current)
    }, [projectId])

    const activeTimer = timers.find(t => t.exited_at === null) || null

    // Group durations by stage_id for lookups
    const durationsByStage = new Map()
    for (const t of timers) {
        const key = t.stage_id
        if (!durationsByStage.has(key)) durationsByStage.set(key, [])
        const isActive = t.exited_at === null
        const durationMs = isActive
            ? Date.now() - new Date(t.entered_at).getTime()
            : (t.duration_ms || 0)
        durationsByStage.get(key).push({ durationMs, isActive, enteredAt: t.entered_at })
    }

    // Live duration string for the active stage
    let liveDuration = ''
    if (activeTimer) {
        const ms = Date.now() - new Date(activeTimer.entered_at).getTime()
        liveDuration = formatDuration(ms)
    }

    return {
        timers,
        activeTimer,
        durationsByStage,
        liveDuration,
        loading,
        refresh,
    }
}
